import { SaveRounded } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import styled from "styled-components";
import { ItemType } from "../../../../client/menuClient";
import { Status } from "../../../../client/status";
import { EditIcon } from "../../../../design-system/Icons";
import { PriceInput } from "../../../PriceInput";
import { useUpdateItemClient } from "./client/updateItemClient";
import { RemoveItem } from "./RemoveItem";
import { UploadItemImage } from "./UpdateItemImage";

const inputBase = `
  background: transparent;
  border: none;
  outline: none;
  font-family: inherit;
  &:enabled { outline: 1px solid #9ecaed; border-radius: 2px; }
  &:focus { outline: 1px solid #9ecaed; }
`;

const ItemNameInput = styled.input`
  ${inputBase}
  flex: 1;
  min-width: 0;
  font-size: 14px;
  padding: 2px 4px;
`;

const DescriptionInput = styled.input`
  ${inputBase}
  width: 100%;
  font-size: 12px;
  color: #666;
  padding: 1px 4px;
  margin-top: 2px;
  box-sizing: border-box;
`;

const priceInputStyle = {
  width: "60px",
  fontSize: "13px",
  backgroundColor: "transparent",
  outline: "none",
  border: "none",
  padding: "2px 2px",
};

export const UpdateItem = ({
  item,
  currency,
  isItemWiseTaxEnabled,
  removeItemFromCategory,
}: {
  item: ItemType;
  currency: string;
  isItemWiseTaxEnabled: boolean;
  removeItemFromCategory: (itemId: number) => void;
}) => {
  const alert = useAlert();

  const [isEditing, setIsEditing] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState(
    item.description ?? ""
  );
  const [itemName, setItemName] = useState(item.name);
  const [itemPrice, setItemPrice] = useState(item.price.toString());
  const [itemTax, setItemTax] = useState(
    item.tax === undefined ? "0" : item.tax.toString()
  );

  const { status, updateItem } = useUpdateItemClient(item.id);

  const saveItem = () => {
    updateItem({
      name: itemName,
      price: parseFloat(itemPrice),
      description: descriptionValue,
      tax: parseFloat(itemTax),
    });
  };

  useEffect(() => {
    if (status === Status.Success) setIsEditing(false);
    if (status === Status.Error)
      alert.error("Request failed. Please try again.");
  }, [alert, status]);

  if (!isEditing) {
    return (
      <Box
        flex={1}
        display="flex"
        alignItems="center"
        minWidth={0}
        px={0.5}
        gap={0.75}
      >
        <UploadItemImage src={item.imageUrl} itemId={item.id} />
        <Box flex={1} minWidth={0}>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            data-testid="item-name-display"
          >
            {itemName}
          </Typography>
          {descriptionValue && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              noWrap
            >
              {descriptionValue}
            </Typography>
          )}
        </Box>
        <Typography
          variant="body2"
          fontWeight={500}
          color="text.secondary"
          sx={{ mx: 1, whiteSpace: "nowrap" }}
        >
          {currency}
          {itemPrice}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setIsEditing(true)}
          data-testid="test-id-edit-item"
        >
          <EditIcon />
        </IconButton>
        <RemoveItem
          itemId={item.id}
          removeItemFromCategory={removeItemFromCategory}
        />
      </Box>
    );
  }

  return (
    <Box
      flex={1}
      display="flex"
      alignItems="flex-start"
      minWidth={0}
      px={0.5}
      gap={0.75}
    >
      <UploadItemImage src={item.imageUrl} itemId={item.id} />
      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="center">
          <ItemNameInput
            type="text"
            placeholder="Item name"
            onChange={(e) => setItemName(e.target.value)}
            value={itemName}
          />

          <Box
            display="flex"
            alignItems="center"
            sx={{
              outline: "1px solid #9ecaed",
              borderRadius: "2px",
              ml: 0.5,
            }}
          >
            <span style={{ fontSize: 13, color: "#555" }}>{currency}</span>
            <PriceInput
              onChange={setItemPrice}
              value={itemPrice}
              placeholder="0.00"
              style={priceInputStyle}
            />
            {isItemWiseTaxEnabled && (
              <>
                <span style={{ fontSize: 12, color: "#888" }}>%</span>
                <PriceInput
                  onChange={setItemTax}
                  value={itemTax}
                  placeholder="tax"
                  style={{ ...priceInputStyle, width: "44px" }}
                />
              </>
            )}
          </Box>

          <IconButton
            onClick={saveItem}
            data-testid="test-id-save-description"
            size="small"
          >
            <SaveRounded color="primary" />
          </IconButton>
          <RemoveItem
            itemId={item.id}
            removeItemFromCategory={removeItemFromCategory}
          />
        </Box>

        <DescriptionInput
          type="text"
          placeholder="Description (optional)"
          value={descriptionValue}
          onChange={(e) => setDescriptionValue(e.target.value)}
        />
      </Box>
    </Box>
  );
};
